// Interface translations. Interface language is kept separate from curriculum
// language and explanation language by design. Swedish is the default.

export const LANGUAGES = [
  { code: "sv", label: "Svenska", short: "SV" },
  { code: "en", label: "English", short: "EN" },
];

export const translations = {
  sv: {
    nav: {
      mission: "Uppdraget",
      model: "Gy25-modellen",
      method: "Metodik",
      roles: "Roller",
      login: "Logga in",
      dashboard: "Min arbetsyta",
      register: "Skapa konto",
    },
    hero: {
      badge: "Reach New Edge™ · Powered by Learning Recovery OS™",
      title: "Varje elev har en ny edge.",
      subtitle:
        "Förstå vad som står i vägen. Hitta nästa motiverade steg. Gå framåt. Ett F, ett fel eller en kunskapslucka är bevis vid en viss tidpunkt — inte elevens identitet.",
      primaryCta: "Kom igång",
      secondaryCta: "Upptäck Reach New Edge",
      identity:
        "Ett F är bevis om en prestation vid en viss tidpunkt — aldrig en elevs identitet.",
    },
    thinks: {
      label: "Så tänker Reach New Edge",
      title: "Från utspridda bevis till ett nästa steg.",
      intro:
        "Reach New Edge frågar inte \u201dVad är fel på eleven?\u201d. Det frågar: vad visar bevisen, vad är fortfarande osäkert, och vad är nästa motiverade steg?",
      stages: [
        { k: "Observerat", d: "Vad som faktiskt hände." },
        { k: "Tolkat", d: "Vad bevisen kan stödja." },
        { k: "Osäkert", d: "Vad som ännu inte är motiverat." },
        { k: "Nästa", d: "Vad som rimligen kan prövas eller kontrolleras." },
      ],
      flow: "Bevis → Förståelse → Nästa motiverade steg → Övning → Omprövning → Framsteg",
    },
    notfound: {
      title: "Den här sidan finns inte.",
      body: "Sidan du sökte kunde inte hittas. Din väg framåt finns fortfarande här.",
      cta: "Till startsidan",
    },
    students: {
      label: "För elever",
      title: "Du är mer än ett resultat.",
      body: "Förstå varför ett nästa steg föreslås. Prova ett annat sätt när den första förklaringen inte räcker. Se vad som ändrats — och fortsätt framåt i små, motiverade steg.",
    },
    teachers: {
      label: "För lärare",
      title: "Bevis → tolkning → osäkerhet → nästa motiverade steg.",
      body: "Reach New Edge stödjer ditt pedagogiska omdöme — det ersätter det inte. AI kan föreslå tolkningar, förklaringar och representationer, men avgör aldrig självständigt läroplanssanning, diagnos, mästerskap eller pedagogiska beslut. Du behåller myndigheten.",
    },
    trust: {
      label: "Principer",
      title: "En produktkonstitution.",
      items: [
        "Bevis är inte identitet.",
        "Observation är inte tolkning.",
        "Osäkerhet är tillåten.",
        "AI-förslag är inte läroplanssanning.",
        "Språklig form är inte detsamma som innehållsförståelse.",
        "En påminnelse är inte bevis.",
        "Framsteg kan omprövas.",
        "Lärare behåller pedagogisk myndighet.",
      ],
    },
    principle: {
      label: "Kärnprincip",
      title: "Från bevis till bemästrande",
      intro:
        "Systemet reduceras aldrig till lektion → prov → poäng. Det är en kedja av pedagogisk intelligens.",
      clickHint: "Välj ett steg för att läsa mer",
    },
    mission: {
      label: "Uppdraget",
      title: "Förstå varför — inte bara vad",
      body: "Den grundläggande frågan är inte \u201Cvilket betyg har eleven?\u201D utan \u201Cvad förstår eleven faktiskt, och vilka förkunskaper saknas?\u201D Vi bygger vägen från elevens nuvarande kunskapsläge mot läroplanens mål.",
      points: [
        "Identifiera exakt vilka kunskaper som saknas",
        "Förstå den underliggande orsaken, inte bara symptomet",
        "Bygg en pedagogisk väg framåt — steg för steg",
      ],
    },
    roles: {
      label: "Roller",
      title: "Byggt för fyra perspektiv",
      subtitle: "Behörighet är alltid explicit och begränsad. Privat som standard.",
      student: {
        name: "Elev",
        desc: "Se var du står, förstå dina kunskapsluckor och följ en trygg väg framåt.",
      },
      teacher: {
        name: "Lärare",
        desc: "Se kunskapsluckor med underlag, skilj AI-slutsats från verifierat bevis.",
      },
      guardian: {
        name: "Vårdnadshavare",
        desc: "Framtida, behörighetsstyrd insyn — reglerad av ålder, samtycke och policy.",
      },
      admin: {
        name: "Skolledare / Ägare",
        desc: "Läroplanskällor, roller, revisionsspår, AI-konfiguration och systemhälsa.",
      },
    },
    provenance: {
      label: "Läroplansproveniens",
      title: "Källa, tolkning och slutsats hålls isär",
      body: "Varje läroplansobjekt bär sin proveniens: jurisdiktion, källa, version och verifieringsstatus. AI-slutsatser blir aldrig tyst auktoritativa fakta.",
      framework: "Ramverk",
      authority: "Källa",
      status: "Verifieringsstatus",
      unverified: "Ej verifierad",
      note: "Läroplanssanning hålls åtskild från AI-förenkling och pedagogisk tolkning.",
    },
    footer: {
      studio: "En lärupplevelse från Felix Lancelot Studios",
      note: "Systemet ersätter aldrig lärare, elevhälsa eller särskilt stöd.",
      foundation: "Varje elev har en ny edge.",
    },
    auth: {
      loginTitle: "Välkommen tillbaka",
      loginSubtitle: "Logga in för att fortsätta din väg framåt.",
      registerTitle: "Skapa ditt konto",
      registerSubtitle: "Börja med ett tryggt, tydligt kunskapsläge.",
      email: "E-post",
      password: "Lösenord",
      name: "Namn",
      role: "Jag är",
      roleStudent: "Elev",
      roleTeacher: "Lärare",
      roleGuardian: "Vårdnadshavare",
      selfRegisterNote: "Registrering skapar ett elevkonto. Lärar- och vårdnadshavarkonton tilldelas av en administratör.",
      loginBtn: "Logga in",
      registerBtn: "Skapa konto",
      google: "Fortsätt med Google",
      or: "eller",
      noAccount: "Har du inget konto?",
      hasAccount: "Har du redan ett konto?",
      goRegister: "Skapa konto",
      goLogin: "Logga in",
      backHome: "Till startsidan",
    },
    dash: {
      welcome: "Välkommen",
      signout: "Logga ut",
      role: "Roll",
      overview: "Översikt",
      foundationNotice:
        "Foundation 1 etablerar arkitekturen. Funktioner nedan aktiveras i kommande foundations — inga påhittade data visas.",
      comingSoon: "Under utveckling",
      comingSoonFull: "Kommer i Foundation 2",
      available: "Aktiv",
      viewAs: "Visa som roll (endast granskning)",
      aiProbe: "Testa AI-anslutning",
      aiProbing: "Testar…",
      aiConnected: "AI-provider ansluten",
      aiFailed: "Anslutning misslyckades",
      auditTitle: "Revisionsspår",
      serviceTitle: "Tjänstegränser",
      provenanceTitle: "Läroplansproveniens",
      modules: {
        where_you_stand: "Var du står",
        knowledge_gaps: "Kunskapsluckor",
        recovery_pathway: "Återhämtningsväg",
        practice_safely: "Öva tryggt",
        progress: "Framsteg",
        request_human_help: "Be om mänsklig hjälp",
        student_learning_gaps: "Elevers kunskapsluckor",
        supporting_evidence: "Underlag och bevis",
        common_misconceptions: "Vanliga missuppfattningar",
        intervention_progress: "Insatsers framsteg",
        human_intervention_signals: "Signaler för mänsklig insats",
        authorized_overview: "Behörig översikt",
        consent_and_permissions: "Samtycke och behörighet",
        curriculum_sources: "Läroplanskällor",
        roles_and_permissions: "Roller och behörighet",
        audit_trails: "Revisionsspår",
        ai_configuration: "AI-konfiguration",
        system_health: "Systemhälsa",
        provenance: "Proveniens",
      },
    },
    curr: {
      title: "Läroplan",
      subtitle: "Officiell Gy25-läroplan från Skolverket. Källfakta hålls åtskilda från AI-förenklingar.",
      navBrowse: "Läroplan",
      navAdmin: "Läroplansadmin",
      framework: "Ramverk",
      subjects: "Ämnen",
      levels: "Nivåer",
      purpose: "Ämnets syfte",
      centralContent: "Centralt innehåll",
      criteria: "Betygskriterier",
      official: "Officiell läroplan",
      officialTag: "Källfakta",
      simpler: "I enklare språk",
      simplify: "Förklara enklare",
      simplifying: "Genererar…",
      aiTag: "AI-slutsats",
      aiDisclaimer: "AI-genererad förenkling – ersätter inte officiell text.",
      readOnly: "Skrivskyddad vy",
      scopeNote: "Verifiering gäller endast denna post – nivåer, centralt innehåll och betygskriterier verifieras var för sig.",
      selectSubject: "Välj ett ämne för att se nivåer och innehåll",
      empty: "Inga ämnen ännu. En administratör behöver köra ingestion.",
      back: "Tillbaka",
      why: "Varför?",
      provenance: "Proveniens",
      source: "Källa",
      snapshot: "Ögonblicksbild",
      reference: "Originalreferens",
      status: {
        UNVERIFIED: "Ej verifierad",
        REVIEW_REQUIRED: "Granskning krävs",
        VERIFIED: "Verifierad",
        REJECTED: "Avvisad",
        RETIRED: "Utfasad",
        CONFLICT: "Konflikt",
      },
      admin: {
        title: "Läroplanskonsol",
        subtitle: "Endast administratör. Styr ingestion, verifiering och konflikter.",
        scopeNote: "Att verifiera ett ämne verifierar endast ämnesposten – inte dess nivåer, centrala innehåll eller betygskriterier.",
        overview: "Översikt",
        runIngest: "Kör ingestion (Gy25)",
        ingesting: "Kör…",
        runs: "Ingestion-körningar",
        subjects: "Ämnen",
        toReview: "Till granskning",
        approve: "Godkänn",
        reject: "Avvisa",
        conflicts: "Konflikter",
        noConflicts: "Inga konflikter",
        parser: "Parser",
        stats: "Statistik",
        run: "Körning",
      },
    },
    kg: {
      navAdmin: "Kunskapsmodell",
      title: "Kunskapsmodell (pedagogisk)",
      subtitle: "Separat pedagogiskt lager. Detta är INTE officiell läroplan – varje påstående bär proveniens.",
      version: "Modellversion",
      publish: "Publicera version",
      tabs: { nodes: "Noder", validation: "Integritet", proposals: "AI-förslag" },
      search: "Sök nod…",
      allTypes: "Alla typer",
      type: "Typ",
      provenance: "Proveniens",
      confidence: "Tillförlitlighet",
      prereqs: "Förkunskaper (in)",
      dependents: "Leder till (ut)",
      mappings: "Läroplanskopplingar",
      unverifiedContext: "Ej verifierad läroplanskontext",
      misconceptions: "Missuppfattningar",
      selectNode: "Välj en nod för detaljer",
      validate: "Kör integritetskontroll",
      healthy: "Grafen är sund",
      issues: "Problem funna",
      orphans: "Föräldralösa noder",
      cycles: "Förkunskapscykler",
      selfEdges: "Självkanter",
      crossSubject: "Ämnesöverskridande förkunskaper",
      duplicates: "Dubbletter",
      generate: "Generera AI-förslag",
      generating: "Genererar…",
      pickLevel: "Välj nivå (MATE)",
      accept: "Acceptera",
      reject: "Avvisa",
      aiProposed: "AI-FÖRSLAG",
      proposalNote: "AI-förslag blir aldrig kanoniska automatiskt – kräver granskning.",
      proposedNodes: "Föreslagna noder",
      studentPlaceholder: "Underliggande kunskapsmodell kommer i en senare lärvy.",
    },
    ks: {
      navView: "Kunskapsläge",
      title: "Kunskapsläge (härlett)",
      subtitle: "Tolkning av bevis — inte betyg, lucka eller bemästrande. Frånvaro av bevis betyder inte att du inte kan.",
      pickStudent: "Välj elev",
      recompute: "Räkna om",
      recomputing: "Räknar om…",
      confidence: "Säkerhet i tolkningen",
      assessed: "Bedömt underlag finns",
      notAssessed: "Inget bedömbart underlag ännu",
      evidenceCount: "Bevisposter",
      supporting: "Stödjande",
      contradicting: "Motsägande",
      ambiguous: "Tvetydiga",
      excluded: "Uteslutna",
      mostRecent: "Senaste bevis",
      why: "Varför tänker appen så här?",
      whyTitle: "Bevisunderlag för tolkningen",
      ruleFired: "Regel som gällde",
      independenceEffect: "Självständighetens betydelse",
      recencyEffect: "Tidsaspektens betydelse",
      considered: "Bevis som övervägdes",
      included: "Bevis som användes",
      excludedWhy: "Bevis som uteslöts (och varför)",
      recomputeTriggers: "Detta gör att tolkningen räknas om",
      engineVersion: "Motorversion",
      modelVersion: "Modellversion",
      close: "Stäng",
      noStates: "Inget härlett kunskapsläge ännu. Tolkningen skapas först när bevis finns.",
      stale: "Kan behöva räknas om",
      notRecommendation: "Detta är en observation av läget, inte en rekommendation eller ett omdöme.",
      disclaimer: "Härlett, deterministiskt lager. Inga luckor, betyg eller bemästrande beräknas här.",
    },
    referral: {
      navView: "Remisser",
      indicatorAria: "Styrda diagnostiska remisser som väntar på granskning",
      title: "Remissinkorg (F6)",
      subtitle: "Språkbaserade innehållsremisser från lärare som väntar på kanonisk F6-diagnostisk granskning.",
      invariant: "En remiss är en begäran om diagnostisk uppmärksamhet — inte en kunskapslucka. F6 äger alla hypoteser och slutsatser.",
      observed: "Observerat",
      why: "Varför remitterad",
      evidence: "Stödjande evidens",
      openReview: "Öppna diagnostisk granskning",
      markReview: "Under granskning",
      markWaiting: "Väntar på evidens",
      markReviewed: "Granskad",
      dismiss: "Avfärda",
      dismissed: "Remissen avfärdades (historik bevaras)",
      empty: "Inga remisser här",
      lane: {
        new: "Nya remisser",
        under_review: "Under diagnostisk granskning",
        waiting: "Väntar på mer evidens",
        resolved: "Granskade / avslutade",
      },
    },
    mylang: {
      nav: "Mitt språk",
      title: "Ditt språk i ditt arbete",
      invariant: "Din språkliga form avgör inte hur väl du förstår ämnet.",
      empty: "Det finns ännu ingen granskad språklig evidens från ditt arbete. När din lärare granskar ett svar dyker stödet upp här.",
      strengthsTitle: "Vad du visade",
      workOnTitle: "Vad vi kan arbeta vidare med",
      tryNextTitle: "Prova det här härnäst",
      whyTitle: "Varför visas det här?",
      whyText: "Det här bygger på granskad evidens i ditt eget arbete — inte på ett omdöme om dig.",
      oralTag: "muntligt",
      k: {
        understood_content: "Du visade att du förstod innehållet.",
        clear_language: "Du uttryckte dig tydligt på språket.",
        communicated_well: "Du fick fram ditt budskap.",
        used_sources: "Du använde källor i ditt svar.",
        work_on_concept: "Vi kan arbeta vidare med själva idén eller innehållet.",
        work_on_form: "Vi kan arbeta vidare med hur idén uttrycks på svenska. Din form behöver inte säga något om hur väl du förstår ämnet.",
        work_on_communication: "Vi kan göra budskapet ännu tydligare.",
        work_on_sources: "Vi kan öva på att använda källor.",
        need_more_example: "Vi behöver ett exempel till för att förstå det här bättre.",
        practice_form: "Prova att skriva om en mening för att göra den tydligare.",
        revisit_concept: "Gå igenom själva idén med ett exempel.",
        keep_practicing: "Fortsätt öva — du är på väg.",
        need_more_evidence: "Det här svaret ensamt räcker inte för att avgöra — vi behöver ett exempel till.",
      },
    },
    aireview: {
      title: "AI-förslag: granskning",
      invariant: "AI får föreslå. Läraren avgör om förslaget blir en accepterad språklig tolkning.",
      pickStudent: "Välj elev",
      original: "Elevens ursprungliga svar",
      proposal: "AI-förslag",
      provenance: "Härkomst",
      grounded: "förankrad",
      ungrounded: "ej förankrad",
      blocked: "Minst ett underlag saknar förankring i elevtexten. Kontrollera innan du accepterar.",
      alternative: "Alternativ tolkning",
      accept: "Acceptera",
      reject: "Avvisa",
      reasonPlaceholder: "Skäl (valfritt)",
      accepted: "Förslaget accepterat",
      rejected: "Förslaget avvisat",
      empty: "Inga AI-förslag att granska för den här eleven.",
      nav: "AI-förslag",
    },
    diag: {
      navView: "Kunskaps-MRI",
      title: "Knowledge Gap MRI™ (diagnostik)",
      subtitle: "Observerad svårighet är inte samma sak som grundorsak. Detta är möjliga förklaringar (hypoteser) — inte fakta om eleven.",
      pickStudent: "Välj elev",
      run: "Kör diagnos igen",
      running: "Kör…",
      difficulty: "Observerad svårighet",
      hypotheses: "Möjliga förklaringar",
      hypothesis: "Hypotes",
      supportedGap: "Underbyggd slutsats (lucka)",
      unresolved: "Ej avgjord – mer underlag behövs",
      statusSupported: "Underlag stödjer",
      statusContradicted: "Underlag talar emot",
      statusUnresolved: "Blandat / osäkert",
      statusInsufficient: "Otillräckligt underlag",
      confidence: "Tillförlitlighet",
      supporting: "Stödjande underlag",
      contradicting: "Motsägande underlag",
      missing: "Saknat underlag",
      probes: "Diagnostiska nästa-steg (probe)",
      probeWhy: "Varför denna kontroll är informativ",
      distinguishes: "Skiljer mellan",
      aiProposed: "AI-föreslagna hypoteser (ej kanoniska)",
      aiPropose: "Föreslå med AI",
      aiProposing: "Föreslår…",
      aiNote: "AI-förslag är tydligt separerade och blir aldrig kanonisk diagnos automatiskt.",
      why: "Varför tänker appen så här?",
      whyTitle: "Spårbarhet: hypotes → kunskapsläge → bevis → observation → svar",
      trace: "Bevisspår",
      graphContext: "Grafkontext",
      curriculumTrace: "Läroplansspår",
      close: "Stäng",
      noData: "Ingen diagnos ännu. Kör diagnosen för att generera hypoteser.",
      noGaps: "Inga underbyggda luckor. Underlaget räcker ännu inte för att fastställa en grundorsak — det är ett giltigt och önskvärt utfall.",
      prereqContext: "Förkunskapsrelation används endast som kontext, inte som bevis.",
      notFact: "Detta presenteras som en möjlig förklaring, inte som ett faktum om eleven.",
      staffOnly: "Endast för lärare/administratör i detta skede.",
    },
    rec: {
      navView: "Återhämtningsväg",
      title: "Återhämtningsväg & insatsplanering",
      subtitle: "Kortaste vägen = minsta befogade insats, inte färrest noder. Vi lär inte ut mer än underlaget motiverar.",
      pickStudent: "Välj elev",
      plan: "Planera om",
      planning: "Planerar…",
      target: "Mål (underbyggd lucka)",
      start: "Startpunkt",
      steps: "Steg",
      strategy: "Insats",
      expected: "Förväntat bevis efteråt",
      skipped: "Hoppas över (behöver inte läras om)",
      alternatives: "Alternativa vägar",
      primary: "Primär",
      freshness: "Aktualitet",
      stale: "Kan behöva räknas om",
      whyRoute: "Varför denna väg?",
      whyStart: "Varför börja här?",
      whyNotReteach: "Varför inte lära om tidigare förkunskaper?",
      whichGap: "Vilken lucka adresseras?",
      minNecessary: "Minsta befogade insats",
      close: "Stäng",
      noRec: "Ingen återhämtningsväg. Mer underlag behövs innan remediering kan rekommenderas.",
      nextSteps: "Diagnostiska nästa steg",
      launchProbe: "Kör riktad kontroll",
      probeResult: "Resultat av kontroll",
      before: "Före",
      after: "Efter",
      constructive: "Vi bygger vidare på det du redan visat — inga onödiga repetitioner.",
      staffOnly: "Endast för lärare/administratör i detta skede.",
    },
    teach: {
      navView: "Lär",
      title: "Lärpass",
      subtitle: "Ett tydligt steg i taget. När något är oklart förklarar vi på ett annat sätt — inte bara upprepar.",
      target: "Vi arbetar med",
      strategy: "Ansats",
      iUnderstand: "Jag förstår",
      explainDifferently: "Förklara annorlunda",
      showExample: "Visa ett exempel",
      giveHint: "Ge en ledtråd",
      letMeTry: "Låt mig försöka",
      needHelp: "Jag behöver hjälp",
      correct: "Jag löste det rätt",
      incorrect: "Jag löste det fel",
      partial: "Delvis rätt",
      yourTurn: "Din tur — prova detta",
      submit: "Skicka försök",
      hint: "Ledtråd",
      whyChanged: "Varför bytte ansatsen?",
      attempted: "Prövade ansatser",
      escalated: "En lärare hjälper dig med detta — det är helt okej.",
      collapsed: "Systemet såg att du redan kan detta, så din väg blev just kortare.",
      noSession: "Inget aktivt lärpass ännu.",
      selfReportNote: "Att säga att du förstår hjälper oss, men vi kollar med ett snabbt försök.",
      close: "Stäng",
    },
    tteach: {
      navView: "Undervisning",
      title: "Lärpass (lärarvy)",
      pick: "Välj elev",
      recoveryTarget: "Återhämtningsmål",
      current: "Nuvarande ansats",
      attempted: "Prövade ansatser",
      support: "Stödnivå",
      escalation: "Eskalering",
      none: "Inget aktivt pass",
      timeline: "Passets tidslinje",
    },
    mi: {
      navView: "Misstag",
      title: "Mistake Intelligence™",
      microcopy: "Ett fel svar är en observation. Ett återkommande mönster är bevis över flera försök — det fastställer inte i sig varför svårigheten uppstod.",
      patterns: "Återkommande mönster",
      patternNote: "bevis över försök, inte en diagnos",
      hypotheses: "Möjliga missuppfattningar",
      observations: "Observerade fel",
      unmapped: "OMAPPAT",
      none: "Inga mönster ännu.",
      notGap: "En möjlig missuppfattning är inte en underbyggd lucka; diagnostiska slutsatser hör till Knowledge Gap MRI™.",
      legend: "Läs uppifrån och ned. Varje lager är mer tolkat än det föregående — ett mönster är aldrig en fastställd diagnos.",
      layer1: "Lager 1 · Observerat fel",
      layer1Note: "Enskilda felaktiga svar. En observation — inte en diagnos.",
      layer2: "Lager 2 · Återkommande mönster",
      layer3: "Lager 3 · Möjlig missuppfattning",
      layer4: "Lager 4 · F6 diagnostisk slutsats",
      layer4Note: "F9 drar ingen diagnos. Diagnostiska slutsatser hör till Knowledge Gap MRI™ (F6).",
      whyPattern: "Varför räknas detta som ett mönster?",
      attempts: "Antal försök",
      distinctTasks: "Olika uppgifter",
      signature: "Gemensam struktur",
      support: "Stödnivå",
      counter: "Motbevis",
      whyConfidence: "Varför denna säkerhet",
      timeline: "Motbevis & livscykel",
      tlEvidence: "Evidens",
      tlInterp: "Systemtolkning",
      tlNote: "Status är en tolkning av evidensen — inte samma sak som evidensen själv.",
      tlError: "Fel",
      tlCorrect: "Självständigt korrekt",
      ambiguity: "Flera rimliga mekanismer bevaras — ingen grundorsak tilldelas av F9.",
      unmappedNote: "Återkommande men inte kopplat till någon modellerad missuppfattning. Ingen missuppfattning uppfinns.",
      noHyp: "Ingen möjlig missuppfattning föreslås.",
    },
    mastery: {
      navView: "Kunskapsstatus",
      navMy: "Min utveckling",
      title: "Mastery Evidence™ (lärarvy)",
      microcopy: "Ett rätt svar är bevis — inte säker kunskap. Säker kunskap är ett bevisbaserat och reversibelt tillstånd, aldrig ett betyg och aldrig permanent.",
      none: "Ingen kunskapsbedömning ännu.",
      notGrade: "Detta är inte ett betyg, ingen procent och ingen prognos. \"Redo att gå vidare\" betyder framsteg i appens sekvens — inte officiellt godkänt.",
      current: "Aktuell status",
      historical: "Historisk demonstration",
      demonstrated: "Visat tidigare",
      notDemonstrated: "Ännu inte demonstrerat",
      tasks: "uppgifter",
      stale: "behöver kollas på nytt",
      why: "Varför denna status?",
      contract: "Kontrakt",
      independence: "Oberoende",
      variation: "Variation",
      transfer: "Transfer",
      retention: "Retention",
      contradiction: "Aktuellt motbevis",
      knowledgeState: "Kunskapsläge",
      ladder_supported_attempt: "Försök med stöd",
      ladder_independent_attempt: "Självständigt försök",
      ladder_variation: "Varierade uppgifter",
      ladder_transfer: "Transfer",
      ladder_retention: "Retention",
      ladder_no_current_contradiction: "Inget aktuellt motbevis",
      prog_CONTINUE_CURRENT_RECOVERY: "Fortsätt pågående återhämtning",
      prog_READY_FOR_INDEPENDENT_REASSESSMENT: "Redo för självständig omprövning",
      prog_MORE_VARIATION_NEEDED: "Fler varierade uppgifter behövs",
      prog_TRANSFER_CHECK_NEEDED: "Transferkontroll behövs",
      prog_RETENTION_CHECK_NEEDED: "Retentionskontroll behövs senare",
      prog_SUFFICIENTLY_DEMONSTRATED_TO_PROGRESS: "Tillräckligt underlag för att gå vidare",
      prog_RETURN_TO_DIAGNOSIS: "Återgå till diagnos",
      prog_HUMAN_REVIEW_RECOMMENDED: "Lärargranskning rekommenderas",
      myTitle: "Min utveckling",
      myMicrocopy: "Här ser du vad ditt underlag visar just nu. Ett rätt svar är en bra början — vi kollar också att kunskapen håller över tid.",
      myNone: "Inget underlag ännu. Det kommer när du övar.",
      myNotGrade: "Detta är inte ett betyg. Det visar vad ditt underlag stödjer just nu.",
      enoughToProgress: "Du har visat detta tillräckligt för att gå vidare.",
      next_CONTINUE_CURRENT_RECOVERY: "Fortsätt öva på det du arbetar med nu.",
      next_READY_FOR_INDEPENDENT_REASSESSMENT: "Prova en uppgift helt själv härnäst.",
      next_MORE_VARIATION_NEEDED: "En annorlunda uppgift till hjälper oss se att det sitter.",
      next_TRANSFER_CHECK_NEEDED: "Prova att använda kunskapen i en ny situation.",
      next_RETENTION_CHECK_NEEDED: "Vi kollar detta igen lite senare.",
      next_RETURN_TO_DIAGNOSIS: "Vi tittar närmare på detta tillsammans.",
      next_HUMAN_REVIEW_RECOMMENDED: "En lärare tittar på detta med dig.",
      next_SUFFICIENTLY_DEMONSTRATED_TO_PROGRESS: "Du har visat detta tillräckligt för att gå vidare.",
    },
    journey: {
      navMy: "Min resa",
      navView: "Läranderesor",
      eyebrow: "Recovery Journey™",
      myTitle: "Din läranderesa",
      myIntro: "Det här är berättelsen om vad som faktiskt hänt i ditt lärande — vad som observerades, vad som ändrades, vad du gjorde själv och vad som händer härnäst.",
      teacherTitle: "Recovery Journey™ (lärarvy)",
      teacherIntro: "En källspårbar projektion av elevens läranderesa. Varje milstolpe härleds ur kanonisk evidens — ingen ny sanning skapas här.",
      empty: "Ingen resa ännu. Den växer fram när det finns underlag.",
      summary: "Sammanfattning",
      notGrade: "Detta är en berättelse baserad på faktiska händelser — inget betyg, ingen procent och ingen ranking.",
      concluded: "Systemets tolkning",
      happened: "Det som hände",
      earlier: "Tidigare",
      reversibleTitle: "Något behöver kollas på nytt",
      reversibleBody: "Din tidigare framgång finns kvar i historiken. Nytt underlag gör att vi vill kontrollera detta igen innan nästa steg.",
      whyMilestone: "Varför visas denna milstolpe?",
      shownBecause: "Milstolpen visas för att den härrör från följande kanoniska källor:",
      historicalNote: "Historisk händelse — bevaras men beskriver inte nödvändigtvis nuläget.",
      nonCanonical: "Icke-kanonisk",
      annotationHint: "Läraranteckning — inte en del av den kanoniska evidenskedjan.",
      noAnnotations: "Inga läraranteckningar.",
      cat_evidence: "Evidenshändelse",
      cat_interpretation: "Systemets tolkning",
      cat_resolved: "Självständig demonstration",
      cat_recheck: "Behöver kollas på nytt",
    },
    pcase: {
      nav: "Elevfall",
      eyebrow: "Pilot Classroom Seam™",
      title: "Elevfall (lärarvy)",
      selectStudent: "Välj elev",
      intro: "En sammansatt, källspårbar vy: vad som observerats, vad systemet tolkat, och vilken nästa handling som är motiverad. Ingen ny sanning skapas här — allt härleds ur kanoniska tjänster.",
      empty: "Inget fall att visa ännu.",
      observed: "Observerat",
      observedBody: "Antal underlag (svar, observationer, kunskapsevidens)",
      interpreted: "Tolkat",
      noGap: "Ingen underbyggd lucka ännu — svårighet är inte samma sak som en lucka.",
      language: "Språk kontra innehåll",
      langInvariant: "SPRÅKLIG FORM ≠ INNEHÅLLSFÖRSTÅELSE ≠ KOMMUNIKATIV FÖRMÅGA ≠ KURSMÅL.",
      referrals: "Remisser till diagnostik",
      referralNote: "En remiss är en begäran om diagnostisk uppmärksamhet — inte en bekräftad kunskapslucka. F6 äger varje slutsats.",
      next: "Nästa motiverade handling",
      noNext: "Ingen motiverad handling just nu.",
      open: "Öppna",
      footer: "Navigeringen är en projektion av kanoniskt tillstånd — inget betyg, ingen ranking, ingen dold elevprofil.",
      act: {
        gather_evidence: "Samla underlag", diagnostic_probe: "Riktad diagnostisk kontroll",
        open_recovery: "Öppna återhämtning", build_recovery: "Planera återhämtning", teach: "Undervisa",
        reassess: "Ompröva", practice_more_variation: "Öva mer variation", return_to_diagnosis: "Återgå till diagnos",
        teacher_review: "Lärargranskning", retention_check: "Kontrollera kvarhållning",
        language_form_support: "Stöd för språklig form", content_diagnosis: "Innehållsdiagnos",
        linguistic_probe: "Språklig kontroll (probe)", review_referral: "Granska remiss",
      },
      prec: {
        WORK_ON_FORM_KEEP_CONCEPT: "Arbeta med språklig form – behåll innehållet",
        TEACH_CONCEPT_NOT_LANGUAGE: "Undervisa innehållet – inte språket",
        MIXED: "Blandad bild – både form och innehåll",
        NEEDS_MORE_EVIDENCE: "Behöver mer underlag",
      },
    },
    scase: {
      nav: "Min översikt",
      eyebrow: "Min översikt",
      title: "Vad jag arbetar med",
      intro: "En vänlig sammanfattning: vad som hänt, vad som ändrats och vad du kan prova härnäst.",
      whatChanged: "Vad som hänt och ändrats",
      beginning: "Resan har börjat — mer underlag behövs.",
      recheck: "Något behöver kollas på nytt innan nästa steg — din tidigare framgång finns kvar.",
      forward: "Det finns nu tillräckligt underlag för att gå vidare.",
      tryNext: "Vad jag kan prova",
      noSteps: "Inga steg just nu.",
      aStep: "Ett steg",
      openJourney: "Öppna min resa",
      footer: "Det här är en berättelse baserad på faktiska händelser — inget betyg och ingen ranking.",
    },
    coview: {
      nav: "Klassöversikt",
      eyebrow: "Klassöversikt",
      title: "Mina elever",
      intro: "En läsvy över dina tilldelade elever. Ingen status, inget betyg, ingen rangordning — bara en väg in till varje elevfall.",
      evidenceYes: "Underlag finns",
      evidenceNo: "Inget underlag ännu",
      nextLabel: "Nästa motiverade handling",
      open: "Öppna fall",
      empty: "Inga tilldelade elever.",
      footer: "Endast elever du är tilldelad. Läsvy — skapar ingen sanning och rangordnar ingen.",
    },
    journey_cc: {
      nav: "Kommandocenter",
      eyebrow: "Recovery Command Center™",
      title: "Beslutsstöd för läraren",
      intro: "Vilka pedagogiska handlingar väntar på dig — och varför? Här visas handlingar, inte rangordnade elever. Antalen räknar arbetsobjekt, inte elever.",
      items: "objekt",
      emptyCat: "Inget här just nu.",
      why: "Varför visas detta?",
      openJourney: "Öppna läranderesan",
      u_window: "Tidsfönster öppet",
      u_due: "Dags för kontroll",
      u_none: "Ej tidskänsligt",
      rechecksTitle: "Återbesök (kontinuitet)",
      rechecksIntro: "Kontextuella arbetsobjekt för schemalagda återbesök. Inte en elevrankning eller efterlevnadsstatus.",
      rk_ready: "Redo för återbesök",
      rk_waiting: "Väntar på tolkning",
      rk_coming: "Planerade",
      rk_missed: "Passerad tid",
      rk_reason: "Schemaläggningsskäl",
      rk_reschedule: "Omplanera",
      rk_reason_ph: "Ange skäl…",
      rk_confirm: "Bekräfta",
      rk_coalesced: "samlade sessioner",
      rk_none: "Inga återbesöksobjekt.",
    },
    rechecks: {
      nav: "Mina återbesök",
      title: "Dags att återvända",
      subtitle: "Korta återbesök för att samla ny evidens på det du redan arbetat med. En planerad kontroll är ett tillfälle att visa vad du kan just nu.",
      tab_ready: "Redo att återbesöka",
      tab_coming: "Kommer senare",
      tab_waiting: "Väntar på resultat",
      state_ready: "Redo att återbesöka",
      state_coming: "Kommer senare",
      state_waiting: "Väntar på resultat",
      state_missed: "Planerad tid passerad",
      empty_ready: "Inget att återbesöka just nu.",
      empty_coming: "Inga planerade återbesök längre fram.",
      empty_waiting: "Inget som väntar på resultat.",
      area: "Lärområde",
      window_now: "Bra tid att göra detta: den här veckan.",
      window_later: "Planeras längre fram.",
      duration: "ca 5 min",
      why_now: "Varför nu?",
      after: "Vad händer sen?",
      after_body: "Du gör en kort uppgift. Den blir ny evidens som systemet tolkar — precis som vanligt arbete.",
      start: "Gör kontrollen",
      completing: "Sparar…",
      do_later: "Gör senare",
      evidence_gathered: "Ny evidens tillagd.",
      result_pending: "Ny evidens är tillagd. Systemet tolkar resultatet — det avgörs inte här.",
      missed_title: "Planerad tid har passerat",
      missed_body: "Den planerade tiden har passerat. Du kan se nästa tillgängliga steg när det dyker upp — inget är förlorat.",
      coalesced_title: "En kort samlad session",
      coalesced_body: "Vi samlade några relaterade kontroller i en kort session. Varje del behåller sin egen koppling till ditt tidigare arbete.",
      prefs: "Påminnelser",
      prefs_title: "Påminnelser och lugna timmar",
      notif_label: "Påminnelser i appen",
      quiet_start: "Lugna timmar från",
      quiet_end: "till",
      tz: "Tidszon",
      prefs_note: "Att stänga av påminnelser tar inte bort skälet till en framtida kontroll — det påverkar bara när du påminns. Extern e-post och push är avstängt.",
      save: "Spara",
      saved: "Sparat.",
      later_done: "Vi påminner dig senare.",
      error: "Något gick fel. Försök igen.",
      policy_note: "Datumet kommer från vår schemaläggningspolicy.",
    },
    langev: {
      nav: "Språkevidens",
      title: "Språkevidens",
      subtitle: "En projektion av accepterad språkevidens. Språklig svårighet är inte samma sak som en kunskapslucka — och detta är ingen språknivå eller poäng.",
      pick_student: "Välj elev",
      load: "Visa",
      region_content: "Vad evidensen säger om innehållet",
      region_language: "Vad evidensen säger om språket",
      region_uncertain: "Vad som är osäkert",
      region_trynext: "Vad man kan prova härnäst",
      why: "Varför?",
      competing: "Konkurrerande tolkningar",
      source: "Källor",
      none: "Inget att visa ännu.",
      invariant: "SPRÅKLIG SVÅRIGHET ≠ KUNSKAPSLUCKA",
      precision_WORK_ON_FORM_KEEP_CONCEPT: "Innehållet är förstått — arbeta med språkformen, återundervisa inte begreppet.",
      precision_TEACH_CONCEPT_NOT_LANGUAGE: "Undervisa begreppet — låt inte polerat språk dölja kunskapsluckan.",
      precision_MIXED_CONTENT_AND_LANGUAGE: "Blandad evidens om både innehåll och språk.",
      precision_NEEDS_MORE_EVIDENCE: "Behöver mer evidens innan en åtgärd är motiverad.",
      send_review: "Skicka till diagnostisk prövning",
      send_note: "Detta skapar inte ett kunskapsgap. Det skickar frågan till den befintliga diagnostiska processen för vidare prövning.",
      sent: "Skickad för diagnostisk prövning",
      oral_open: "Lägg till muntlig evidens",
      oral_title: "Muntlig evidens",
      oral_desc: "Registrera vad eleven faktiskt sa (lärarobservation) eller en transkription. Ingen ljudinspelning, ingen tal-till-text, ingen uttals- eller flytbedömning.",
      oral_badge: "MUNTLIG",
      oral_source: "Källa",
      oral_teacher_obs: "Lärarobservation",
      oral_transcript: "Transkription",
      oral_subject: "Ämne",
      oral_text: "Ursprunglig muntlig produktion (oförändrad)",
      oral_text_ph: "Skriv exakt vad eleven sa …",
      oral_context: "Uppgift/sammanhang (valfritt)",
      oral_submit: "Spara muntlig evidens",
      oral_saved: "Muntlig evidens sparad — originalet är oförändrat",
    },
    common: {
      loading: "Laddar…",
      error: "Något gick fel. Försök igen.",
    },
  },

  en: {
    nav: {
      mission: "Mission",
      model: "The Gy25 model",
      method: "Methodology",
      roles: "Roles",
      login: "Log in",
      dashboard: "My workspace",
      register: "Create account",
    },
    hero: {
      badge: "Reach New Edge™ · Powered by Learning Recovery OS™",
      title: "Every learner has a next edge.",
      subtitle:
        "Understand what stands in the way. Find the next justified step. Move forward. An F, a wrong answer or a knowledge gap is evidence at a moment in time — not the learner's identity.",
      primaryCta: "Get started",
      secondaryCta: "Discover Reach New Edge",
      identity:
        "An F is evidence about performance at a point in time — never a student's identity.",
    },
    thinks: {
      label: "How Reach New Edge thinks",
      title: "From scattered evidence to a next step.",
      intro:
        "Reach New Edge does not ask \u201cWhat is wrong with this learner?\u201d It asks: what does the evidence show, what remains uncertain, and what is the next justified step?",
      stages: [
        { k: "Observed", d: "What actually happened." },
        { k: "Interpreted", d: "What the evidence may support." },
        { k: "Uncertain", d: "What is not yet justified." },
        { k: "Next", d: "What can reasonably be tried or checked." },
      ],
      flow: "Evidence → Understanding → Next justified step → Practice → Reassessment → Progress",
    },
    notfound: {
      title: "This page doesn't exist.",
      body: "The page you were looking for couldn't be found. Your path forward is still here.",
      cta: "Back to home",
    },
    students: {
      label: "For students",
      title: "You are more than one result.",
      body: "Understand why a next step is suggested. Try another way when the first explanation isn't enough. See what changed — and keep moving forward in small, justified steps.",
    },
    teachers: {
      label: "For teachers",
      title: "Evidence → interpretation → uncertainty → next justified action.",
      body: "Reach New Edge supports your pedagogical reasoning — it does not replace it. AI may assist with proposals, explanations and representations, but never independently establishes curriculum truth, diagnosis, mastery or educational decisions. You retain authority.",
    },
    trust: {
      label: "Principles",
      title: "A product constitution.",
      items: [
        "Evidence is not identity.",
        "Observation is not interpretation.",
        "Uncertainty is allowed.",
        "AI proposals are not educational truth.",
        "Language form is not the same as content understanding.",
        "A reminder is not evidence.",
        "Progress can be reassessed.",
        "Teachers retain pedagogical authority.",
      ],
    },
    principle: {
      label: "Core principle",
      title: "From evidence to mastery",
      intro:
        "The system is never reduced to lesson → quiz → score. It is a chain of pedagogical intelligence.",
      clickHint: "Select a step to read more",
    },
    mission: {
      label: "Mission",
      title: "Understand why — not just what",
      body: "The fundamental question is not \u201Cwhat grade does the student have?\u201D but \u201Cwhat does the student actually understand, and which prerequisites are missing?\u201D We build the pathway from the present knowledge state toward curriculum mastery.",
      points: [
        "Identify exactly which knowledge is missing",
        "Understand the root cause, not just the symptom",
        "Build a pedagogical pathway forward — step by step",
      ],
    },
    roles: {
      label: "Roles",
      title: "Built for four perspectives",
      subtitle: "Access is always explicit and least-privilege. Private by default.",
      student: {
        name: "Student",
        desc: "See where you stand, understand your gaps, and follow a safe path forward.",
      },
      teacher: {
        name: "Teacher",
        desc: "See gaps with supporting evidence; distinguish AI inference from verified evidence.",
      },
      guardian: {
        name: "Guardian",
        desc: "Future authorization-governed visibility — regulated by age, consent and policy.",
      },
      admin: {
        name: "Admin / Owner",
        desc: "Curriculum sources, roles, audit trails, AI configuration and system health.",
      },
    },
    provenance: {
      label: "Curriculum provenance",
      title: "Source, interpretation and inference stay separate",
      body: "Every curriculum object carries its provenance: jurisdiction, source, version and verification status. AI inference never silently becomes authoritative fact.",
      framework: "Framework",
      authority: "Source",
      status: "Verification status",
      unverified: "Unverified",
      note: "Curriculum truth is kept separate from AI simplification and pedagogical inference.",
    },
    footer: {
      studio: "A learning experience by Felix Lancelot Studios",
      note: "This system never replaces teachers, student health services or special support.",
      foundation: "Every learner has a next edge.",
    },
    auth: {
      loginTitle: "Welcome back",
      loginSubtitle: "Log in to continue your path forward.",
      registerTitle: "Create your account",
      registerSubtitle: "Begin with a safe, clear knowledge state.",
      email: "Email",
      password: "Password",
      name: "Name",
      role: "I am a",
      roleStudent: "Student",
      roleTeacher: "Teacher",
      roleGuardian: "Guardian",
      selfRegisterNote: "Registration creates a student account. Teacher and guardian accounts are assigned by an administrator.",
      loginBtn: "Log in",
      registerBtn: "Create account",
      google: "Continue with Google",
      or: "or",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      goRegister: "Create account",
      goLogin: "Log in",
      backHome: "Back to home",
    },
    dash: {
      welcome: "Welcome",
      signout: "Sign out",
      role: "Role",
      overview: "Overview",
      foundationNotice:
        "Foundation 1 establishes the architecture. The capabilities below activate in later foundations — no fabricated data is shown.",
      comingSoon: "In development",
      comingSoonFull: "Coming in Foundation 2",
      available: "Active",
      viewAs: "View as role (review only)",
      aiProbe: "Test AI connectivity",
      aiProbing: "Testing…",
      aiConnected: "AI provider connected",
      aiFailed: "Connection failed",
      auditTitle: "Audit trail",
      serviceTitle: "Service boundaries",
      provenanceTitle: "Curriculum provenance",
      modules: {
        where_you_stand: "Where you stand",
        knowledge_gaps: "Knowledge gaps",
        recovery_pathway: "Recovery pathway",
        practice_safely: "Practice safely",
        progress: "Progress",
        request_human_help: "Request human help",
        student_learning_gaps: "Student learning gaps",
        supporting_evidence: "Supporting evidence",
        common_misconceptions: "Common misconceptions",
        intervention_progress: "Intervention progress",
        human_intervention_signals: "Human-intervention signals",
        authorized_overview: "Authorized overview",
        consent_and_permissions: "Consent and permissions",
        curriculum_sources: "Curriculum sources",
        roles_and_permissions: "Roles and permissions",
        audit_trails: "Audit trails",
        ai_configuration: "AI configuration",
        system_health: "System health",
        provenance: "Provenance",
      },
    },
    curr: {
      title: "Curriculum",
      subtitle: "Official Gy25 curriculum from Skolverket. Source facts are kept separate from AI simplifications.",
      navBrowse: "Curriculum",
      navAdmin: "Curriculum admin",
      framework: "Framework",
      subjects: "Subjects",
      levels: "Levels",
      purpose: "Subject purpose",
      centralContent: "Central content",
      criteria: "Grade criteria",
      official: "Official curriculum",
      officialTag: "Source fact",
      simpler: "In simpler language",
      simplify: "Explain more simply",
      simplifying: "Generating…",
      aiTag: "AI inference",
      aiDisclaimer: "AI-generated simplification – does not replace official text.",
      readOnly: "Read-only view",
      scopeNote: "Verification applies to this record only – levels, central content and grade criteria are verified separately.",
      selectSubject: "Select a subject to see levels and content",
      empty: "No subjects yet. An admin needs to run ingestion.",
      back: "Back",
      why: "Why?",
      provenance: "Provenance",
      source: "Source",
      snapshot: "Snapshot",
      reference: "Original reference",
      status: {
        UNVERIFIED: "Unverified",
        REVIEW_REQUIRED: "Review required",
        VERIFIED: "Verified",
        REJECTED: "Rejected",
        RETIRED: "Retired",
        CONFLICT: "Conflict",
      },
      admin: {
        title: "Curriculum console",
        subtitle: "Admin only. Govern ingestion, verification and conflicts.",
        scopeNote: "Verifying a subject verifies only the subject record – not its levels, central content or grade criteria.",
        overview: "Overview",
        runIngest: "Run ingestion (Gy25)",
        ingesting: "Running…",
        runs: "Ingestion runs",
        subjects: "Subjects",
        toReview: "To review",
        approve: "Approve",
        reject: "Reject",
        conflicts: "Conflicts",
        noConflicts: "No conflicts",
        parser: "Parser",
        stats: "Stats",
        run: "Run",
      },
    },
    kg: {
      navAdmin: "Knowledge model",
      title: "Knowledge model (pedagogical)",
      subtitle: "Separate pedagogical layer. This is NOT official curriculum – every claim carries provenance.",
      version: "Model version",
      publish: "Publish version",
      tabs: { nodes: "Nodes", validation: "Integrity", proposals: "AI proposals" },
      search: "Search node…",
      allTypes: "All types",
      type: "Type",
      provenance: "Provenance",
      confidence: "Confidence",
      prereqs: "Prerequisites (in)",
      dependents: "Leads to (out)",
      mappings: "Curriculum mappings",
      unverifiedContext: "Unverified curriculum context",
      misconceptions: "Misconceptions",
      selectNode: "Select a node for details",
      validate: "Run integrity check",
      healthy: "Graph is healthy",
      issues: "Issues found",
      orphans: "Orphan nodes",
      cycles: "Prerequisite cycles",
      selfEdges: "Self-edges",
      crossSubject: "Cross-subject prerequisites",
      duplicates: "Duplicates",
      generate: "Generate AI proposal",
      generating: "Generating…",
      pickLevel: "Pick level (MATE)",
      accept: "Accept",
      reject: "Reject",
      aiProposed: "AI-PROPOSED",
      proposalNote: "AI proposals never become canonical automatically – they require review.",
      proposedNodes: "Proposed nodes",
      studentPlaceholder: "Underlying knowledge model coming in a later learning view.",
    },
    ks: {
      navView: "Knowledge state",
      title: "Knowledge state (derived)",
      subtitle: "An interpretation of evidence — not a grade, gap or mastery. Absence of evidence does not mean you cannot do it.",
      pickStudent: "Select student",
      recompute: "Recompute",
      recomputing: "Recomputing…",
      confidence: "Confidence in the interpretation",
      assessed: "Assessable evidence present",
      notAssessed: "No assessable evidence yet",
      evidenceCount: "Evidence records",
      supporting: "Supporting",
      contradicting: "Contradicting",
      ambiguous: "Ambiguous",
      excluded: "Excluded",
      mostRecent: "Most recent evidence",
      why: "Why does the app think this?",
      whyTitle: "Evidence chain behind the interpretation",
      ruleFired: "Rule applied",
      independenceEffect: "Effect of independence",
      recencyEffect: "Effect of recency",
      considered: "Evidence considered",
      included: "Evidence used",
      excludedWhy: "Evidence excluded (and why)",
      recomputeTriggers: "What causes this to be recomputed",
      engineVersion: "Engine version",
      modelVersion: "Model version",
      close: "Close",
      noStates: "No derived knowledge state yet. An interpretation is only created once evidence exists.",
      stale: "May need recomputing",
      notRecommendation: "This is an observation of state, not a recommendation or judgement.",
      disclaimer: "Derived, deterministic layer. No gaps, grades or mastery are computed here.",
    },
    referral: {
      navView: "Referrals",
      indicatorAria: "Governed diagnostic referrals waiting for review",
      title: "Referral Inbox (F6)",
      subtitle: "Language-derived content referrals from teachers awaiting canonical F6 diagnostic review.",
      invariant: "A referral is a request for diagnostic attention — not a knowledge gap. F6 owns all hypotheses and conclusions.",
      observed: "Observed",
      why: "Why referred",
      evidence: "Supporting evidence",
      openReview: "Open diagnostic review",
      markReview: "Under review",
      markWaiting: "Waiting for evidence",
      markReviewed: "Reviewed",
      dismiss: "Dismiss",
      dismissed: "Referral dismissed (history preserved)",
      empty: "No referrals here",
      lane: {
        new: "New referrals",
        under_review: "Under diagnostic review",
        waiting: "Waiting for more evidence",
        resolved: "Reviewed / resolved",
      },
    },
    mylang: {
      nav: "My language",
      title: "Your language in your work",
      invariant: "Your language form does not decide how well you understand the subject.",
      empty: "There is no reviewed language evidence from your work yet. When your teacher reviews a response, support will appear here.",
      strengthsTitle: "What you showed",
      workOnTitle: "What we can work on next",
      tryNextTitle: "Try this next",
      whyTitle: "Why is this shown?",
      whyText: "This is based on reviewed evidence in your own work — not a judgment about you.",
      oralTag: "spoken",
      k: {
        understood_content: "You showed that you understood the content.",
        clear_language: "You expressed yourself clearly in the language.",
        communicated_well: "You got your message across.",
        used_sources: "You used sources in your answer.",
        work_on_concept: "We can work more on the idea or the content itself.",
        work_on_form: "We can work on how the idea is expressed. Your language form does not have to say anything about how well you understand the subject.",
        work_on_communication: "We can make the message even clearer.",
        work_on_sources: "We can practise using sources.",
        need_more_example: "We need one more example to understand this better.",
        practice_form: "Try rewriting one sentence to make it clearer.",
        revisit_concept: "Revisit the idea itself with an example.",
        keep_practicing: "Keep practising — you're on your way.",
        need_more_evidence: "This answer alone is not enough to tell — we need one more example.",
      },
    },
    aireview: {
      title: "AI proposal review",
      invariant: "The AI may propose. The teacher decides whether the proposal becomes an accepted linguistic interpretation.",
      pickStudent: "Choose student",
      original: "Original student response",
      proposal: "AI proposal",
      provenance: "Provenance",
      grounded: "grounded",
      ungrounded: "not grounded",
      blocked: "At least one signal is not grounded in the student text. Verify before accepting.",
      alternative: "Alternative interpretation",
      accept: "Accept",
      reject: "Reject",
      reasonPlaceholder: "Reason (optional)",
      accepted: "Proposal accepted",
      rejected: "Proposal rejected",
      empty: "No AI proposals to review for this student.",
      nav: "AI proposals",
    },
    diag: {
      navView: "Knowledge MRI",
      title: "Knowledge Gap MRI™ (diagnostic)",
      subtitle: "Observed difficulty is not the same as root cause. These are possible explanations (hypotheses) — not facts about the learner.",
      pickStudent: "Select student",
      run: "Re-run diagnosis",
      running: "Running…",
      difficulty: "Observed difficulty",
      hypotheses: "Possible explanations",
      hypothesis: "Hypothesis",
      supportedGap: "Supported conclusion (gap)",
      unresolved: "Unresolved – more evidence needed",
      statusSupported: "Evidence supports",
      statusContradicted: "Evidence contradicts",
      statusUnresolved: "Mixed / uncertain",
      statusInsufficient: "Insufficient evidence",
      confidence: "Confidence",
      supporting: "Supporting evidence",
      contradicting: "Contradicting evidence",
      missing: "Missing evidence",
      probes: "Diagnostic next-step probes",
      probeWhy: "Why this check is informative",
      distinguishes: "Distinguishes between",
      aiProposed: "AI-proposed hypotheses (non-canonical)",
      aiPropose: "Propose with AI",
      aiProposing: "Proposing…",
      aiNote: "AI proposals are clearly separated and never become canonical diagnosis automatically.",
      why: "Why does the app think this?",
      whyTitle: "Traceability: hypothesis → knowledge state → evidence → observation → response",
      trace: "Evidence trace",
      graphContext: "Graph context",
      curriculumTrace: "Curriculum trace",
      close: "Close",
      noData: "No diagnosis yet. Run the diagnosis to generate hypotheses.",
      noGaps: "No supported gaps. Evidence is not yet sufficient to establish a root cause — a valid and desirable outcome.",
      prereqContext: "A prerequisite relationship is used only as context, never as proof.",
      notFact: "This is presented as a possible explanation, not as a fact about the learner.",
      staffOnly: "Teacher/admin only at this stage.",
    },
    rec: {
      navView: "Recovery path",
      title: "Recovery path & intervention planning",
      subtitle: "Shortest route = smallest justified intervention, not fewest nodes. We teach no more than the evidence justifies.",
      pickStudent: "Select student",
      plan: "Re-plan",
      planning: "Planning…",
      target: "Target (supported gap)",
      start: "Starting point",
      steps: "Steps",
      strategy: "Intervention",
      expected: "Expected evidence afterward",
      skipped: "Skipped (no reteaching needed)",
      alternatives: "Alternative routes",
      primary: "Primary",
      freshness: "Freshness",
      stale: "May need recomputing",
      whyRoute: "Why this route?",
      whyStart: "Why start here?",
      whyNotReteach: "Why not reteach earlier prerequisites?",
      whichGap: "Which gap is addressed?",
      minNecessary: "Minimum necessary intervention",
      close: "Close",
      noRec: "No recovery path. More evidence is needed before recommending remediation.",
      nextSteps: "Diagnostic next steps",
      launchProbe: "Run targeted check",
      probeResult: "Probe result",
      before: "Before",
      after: "After",
      constructive: "We build on what you have already shown — no unnecessary repetition.",
      staffOnly: "Teacher/admin only at this stage.",
    },
    teach: {
      navView: "Learn",
      title: "Learning session",
      subtitle: "One clear step at a time. When something is unclear, we explain it a different way — not just repeat it.",
      target: "We're working on",
      strategy: "Approach",
      iUnderstand: "I understand",
      explainDifferently: "Explain differently",
      showExample: "Show me an example",
      giveHint: "Give me a hint",
      letMeTry: "Let me try",
      needHelp: "I need help",
      correct: "I got it right",
      incorrect: "I got it wrong",
      partial: "Partly right",
      yourTurn: "Your turn — try this",
      submit: "Submit attempt",
      hint: "Hint",
      whyChanged: "Why did the approach change?",
      attempted: "Approaches tried",
      escalated: "A teacher will help you with this — that's completely okay.",
      collapsed: "The system saw you already know this, so your route just got shorter.",
      noSession: "No active learning session yet.",
      selfReportNote: "Saying you understand helps us, but we'll check with a quick try.",
      close: "Close",
    },
    tteach: {
      navView: "Teaching",
      title: "Teaching session (teacher view)",
      pick: "Select student",
      recoveryTarget: "Recovery target",
      current: "Current approach",
      attempted: "Approaches tried",
      support: "Support level",
      escalation: "Escalation",
      none: "No active session",
      timeline: "Session timeline",
    },
    mi: {
      navView: "Mistakes",
      title: "Mistake Intelligence™",
      microcopy: "A wrong answer is an observation. A recurring pattern is evidence across attempts — it does not by itself establish why the difficulty occurred.",
      patterns: "Recurring patterns",
      patternNote: "evidence across attempts, not a diagnosis",
      hypotheses: "Possible misconceptions",
      observations: "Observed errors",
      unmapped: "UNMAPPED",
      none: "No patterns yet.",
      notGap: "A possible misconception is not a supported gap; diagnostic conclusions belong to the Knowledge Gap MRI™.",
      legend: "Read top to bottom. Each layer is more interpreted than the one above — a pattern is never an established diagnosis.",
      layer1: "Layer 1 · Observed error",
      layer1Note: "Individual wrong answers. An observation — not a diagnosis.",
      layer2: "Layer 2 · Recurring pattern",
      layer3: "Layer 3 · Possible misconception",
      layer4: "Layer 4 · F6 diagnostic conclusion",
      layer4Note: "F9 draws no diagnosis. Diagnostic conclusions belong to the Knowledge Gap MRI™ (F6).",
      whyPattern: "Why is this considered a pattern?",
      attempts: "Attempts",
      distinctTasks: "Distinct tasks",
      signature: "Common structure",
      support: "Support level",
      counter: "Counterevidence",
      whyConfidence: "Why this confidence",
      timeline: "Counterevidence & lifecycle",
      tlEvidence: "Evidence",
      tlInterp: "System interpretation",
      tlNote: "Status is an interpretation of the evidence — not the evidence itself.",
      tlError: "Error",
      tlCorrect: "Independently correct",
      ambiguity: "Multiple plausible mechanisms are preserved — F9 assigns no root cause.",
      unmappedNote: "Recurring but not linked to any modeled misconception. No misconception is fabricated.",
      noHyp: "No possible misconception proposed.",
    },
    mastery: {
      navView: "Mastery status",
      navMy: "My progress",
      title: "Mastery Evidence™ (teacher view)",
      microcopy: "A correct answer is evidence — not mastery. Mastery is an evidence-based, reversible state, never a grade and never permanent.",
      none: "No mastery assessment yet.",
      notGrade: "This is not a grade, a percentage or a prediction. \"Ready to progress\" means moving forward in the app's sequence — not an official pass.",
      current: "Current status",
      historical: "Historical demonstration",
      demonstrated: "Demonstrated before",
      notDemonstrated: "Not yet demonstrated",
      tasks: "tasks",
      stale: "worth checking again",
      why: "Why this status?",
      contract: "Contract",
      independence: "Independence",
      variation: "Variation",
      transfer: "Transfer",
      retention: "Retention",
      contradiction: "Current contradiction",
      knowledgeState: "Knowledge state",
      ladder_supported_attempt: "Supported attempt",
      ladder_independent_attempt: "Independent attempt",
      ladder_variation: "Varied tasks",
      ladder_transfer: "Transfer",
      ladder_retention: "Retention",
      ladder_no_current_contradiction: "No current contradiction",
      prog_CONTINUE_CURRENT_RECOVERY: "Continue current recovery",
      prog_READY_FOR_INDEPENDENT_REASSESSMENT: "Ready for independent reassessment",
      prog_MORE_VARIATION_NEEDED: "More varied tasks needed",
      prog_TRANSFER_CHECK_NEEDED: "Transfer check needed",
      prog_RETENTION_CHECK_NEEDED: "Retention check needed later",
      prog_SUFFICIENTLY_DEMONSTRATED_TO_PROGRESS: "Enough evidence to progress",
      prog_RETURN_TO_DIAGNOSIS: "Return to diagnosis",
      prog_HUMAN_REVIEW_RECOMMENDED: "Human review recommended",
      myTitle: "My progress",
      myMicrocopy: "Here is what your evidence shows right now. One correct answer is a good start — we also check that the knowledge lasts over time.",
      myNone: "No evidence yet. It appears as you practise.",
      myNotGrade: "This is not a grade. It shows what your evidence supports right now.",
      enoughToProgress: "You've shown this enough to move forward.",
      next_CONTINUE_CURRENT_RECOVERY: "Keep practising what you're working on.",
      next_READY_FOR_INDEPENDENT_REASSESSMENT: "Try a task fully on your own next.",
      next_MORE_VARIATION_NEEDED: "One more different task will help show it's stable.",
      next_TRANSFER_CHECK_NEEDED: "Try using the knowledge in a new situation.",
      next_RETENTION_CHECK_NEEDED: "We'll check this again a little later.",
      next_RETURN_TO_DIAGNOSIS: "Let's look at this together more closely.",
      next_HUMAN_REVIEW_RECOMMENDED: "A teacher will look at this with you.",
      next_SUFFICIENTLY_DEMONSTRATED_TO_PROGRESS: "You've shown this enough to move forward.",
    },
    journey: {
      navMy: "My journey",
      navView: "Learning journeys",
      eyebrow: "Recovery Journey™",
      myTitle: "Your learning journey",
      myIntro: "This is the story of what actually happened in your learning — what was observed, what changed, what you did on your own, and what happens next.",
      teacherTitle: "Recovery Journey™ (teacher view)",
      teacherIntro: "A source-traceable projection of the learner's journey. Every milestone is derived from canonical evidence — no new truth is created here.",
      empty: "No journey yet. It grows as evidence appears.",
      summary: "Summary",
      notGrade: "This is a story based on things that actually happened — not a grade, a percentage or a ranking.",
      concluded: "What the system concluded",
      happened: "What happened",
      earlier: "Earlier",
      reversibleTitle: "Something needs checking again",
      reversibleBody: "Your earlier success is preserved in the history. New evidence means we want to check this again before the next step.",
      whyMilestone: "Why is this milestone shown?",
      shownBecause: "This milestone is shown because it derives from the following canonical sources:",
      historicalNote: "Historical event — preserved, but does not necessarily describe the current state.",
      nonCanonical: "Non-canonical",
      annotationHint: "Teacher annotation — not part of the canonical evidence chain.",
      noAnnotations: "No teacher annotations.",
      cat_evidence: "Evidence event",
      cat_interpretation: "System interpretation",
      cat_resolved: "Independent demonstration",
      cat_recheck: "Needs rechecking",
    },
    pcase: {
      nav: "Student case",
      eyebrow: "Pilot Classroom Seam™",
      title: "Student case (teacher view)",
      selectStudent: "Select student",
      intro: "A composed, source-traceable view: what was observed, what the system interpreted, and which next action is justified. No new truth is created here — everything is derived from canonical services.",
      empty: "No case to show yet.",
      observed: "Observed",
      observedBody: "Evidence count (responses, observations, knowledge evidence)",
      interpreted: "Interpreted",
      noGap: "No supported gap yet — a difficulty is not the same as a gap.",
      language: "Language vs content",
      langInvariant: "LINGUISTIC FORM ≠ CONTENT UNDERSTANDING ≠ COMMUNICATIVE CAPABILITY ≠ CURRICULUM ACHIEVEMENT.",
      referrals: "Diagnostic referrals",
      referralNote: "A referral is a request for diagnostic attention — not a confirmed knowledge gap. F6 owns every conclusion.",
      next: "Next justified action",
      noNext: "No justified action right now.",
      open: "Open",
      footer: "Navigation is a projection of canonical state — no grade, no ranking, no hidden learner profile.",
      act: {
        gather_evidence: "Gather evidence", diagnostic_probe: "Targeted diagnostic probe",
        open_recovery: "Open recovery", build_recovery: "Plan recovery", teach: "Teach",
        reassess: "Reassess", practice_more_variation: "Practice more variation", return_to_diagnosis: "Return to diagnosis",
        teacher_review: "Teacher review", retention_check: "Retention check",
        language_form_support: "Language-form support", content_diagnosis: "Content diagnosis",
        linguistic_probe: "Linguistic probe", review_referral: "Review referral",
      },
      prec: {
        WORK_ON_FORM_KEEP_CONCEPT: "Work on language form — keep the concept",
        TEACH_CONCEPT_NOT_LANGUAGE: "Teach the concept — not the language",
        MIXED: "Mixed — both form and content",
        NEEDS_MORE_EVIDENCE: "Needs more evidence",
      },
    },
    scase: {
      nav: "My overview",
      eyebrow: "My overview",
      title: "What I'm working on",
      intro: "A friendly summary: what happened, what changed, and what you can try next.",
      whatChanged: "What happened and changed",
      beginning: "The journey has begun — more evidence is needed.",
      recheck: "Something needs checking again before the next step — your earlier success is preserved.",
      forward: "There is now enough evidence to move forward.",
      tryNext: "What I can try",
      noSteps: "No steps right now.",
      aStep: "A step",
      openJourney: "Open my journey",
      footer: "This is a story based on things that actually happened — not a grade or a ranking.",
    },
    coview: {
      nav: "Classroom",
      eyebrow: "Classroom overview",
      title: "My students",
      intro: "A read-only view of the students assigned to you. No status, no grade, no ranking — just a way into each student's case.",
      evidenceYes: "Evidence exists",
      evidenceNo: "No evidence yet",
      nextLabel: "Next justified action",
      open: "Open case",
      empty: "No assigned students.",
      footer: "Only students you are assigned to. Read-only — creates no truth and ranks no one.",
    },
    journey_cc: {
      nav: "Command Center",
      eyebrow: "Recovery Command Center™",
      title: "Teacher decision support",
      intro: "Which pedagogical actions are waiting for you — and why? This shows actions, not ranked students. Counts count work items, not students.",
      items: "items",
      emptyCat: "Nothing here right now.",
      why: "Why is this here?",
      openJourney: "Open recovery journey",
      u_window: "Time window open",
      u_due: "Due for review",
      u_none: "Not time-sensitive",
      rechecksTitle: "Rechecks (continuity)",
      rechecksIntro: "Contextual work items for scheduled rechecks. Not a student ranking or compliance status.",
      rk_ready: "Ready for recheck",
      rk_waiting: "Awaiting interpretation",
      rk_coming: "Planned",
      rk_missed: "Time passed",
      rk_reason: "Scheduling reason",
      rk_reschedule: "Reschedule",
      rk_reason_ph: "Enter reason…",
      rk_confirm: "Confirm",
      rk_coalesced: "coalesced sessions",
      rk_none: "No recheck items.",
    },
    rechecks: {
      nav: "My rechecks",
      title: "A good time to revisit",
      subtitle: "Brief revisits to gather new evidence about what you've already worked on. A scheduled check is a chance to show what you can do now.",
      tab_ready: "Ready to revisit",
      tab_coming: "Coming later",
      tab_waiting: "Waiting for result",
      state_ready: "Ready to revisit",
      state_coming: "Coming later",
      state_waiting: "Waiting for result",
      state_missed: "Planned time passed",
      empty_ready: "Nothing to revisit right now.",
      empty_coming: "No revisits planned for later.",
      empty_waiting: "Nothing waiting for a result.",
      area: "Learning area",
      window_now: "A good time to do this: this week.",
      window_later: "Planned for a bit later.",
      duration: "approx. 5 min",
      why_now: "Why now?",
      after: "What happens next?",
      after_body: "You do a short task. It becomes new evidence the system interprets — just like ordinary work.",
      start: "Do the check",
      completing: "Saving…",
      do_later: "Do later",
      evidence_gathered: "New evidence gathered.",
      result_pending: "New evidence has been added. The system is interpreting the result — it isn't decided here.",
      missed_title: "The planned time has passed",
      missed_body: "The planned time has passed. You'll see the next available step when it appears — nothing is lost.",
      coalesced_title: "One short combined session",
      coalesced_body: "We grouped a few related checks into one short session. Each part keeps its own link to your earlier work.",
      prefs: "Reminders",
      prefs_title: "Reminders and quiet hours",
      notif_label: "In-app reminders",
      quiet_start: "Quiet hours from",
      quiet_end: "to",
      tz: "Timezone",
      prefs_note: "Turning off reminders does not remove the reason for a future check — it only changes when you're reminded. External email and push are off.",
      save: "Save",
      saved: "Saved.",
      later_done: "We'll remind you later.",
      error: "Something went wrong. Please try again.",
      policy_note: "This date comes from our scheduling policy.",
    },
    langev: {
      nav: "Language evidence",
      title: "Language evidence",
      subtitle: "A projection of accepted language evidence. Linguistic difficulty is not the same as a knowledge gap — and this is not a language level or score.",
      pick_student: "Choose student",
      load: "Show",
      region_content: "What the evidence suggests about the content",
      region_language: "What the evidence suggests about language",
      region_uncertain: "What remains uncertain",
      region_trynext: "What to try next",
      why: "Why?",
      competing: "Competing interpretations",
      source: "Sources",
      none: "Nothing to show yet.",
      invariant: "LINGUISTIC DIFFICULTY ≠ KNOWLEDGE GAP",
      precision_WORK_ON_FORM_KEEP_CONCEPT: "Content understood — work on language form; do not re-teach the concept.",
      precision_TEACH_CONCEPT_NOT_LANGUAGE: "Teach the concept — don't let polished language mask the gap.",
      precision_MIXED_CONTENT_AND_LANGUAGE: "Mixed evidence about both content and language.",
      precision_NEEDS_MORE_EVIDENCE: "Needs more evidence before an action is justified.",
      send_review: "Send for diagnostic review",
      send_note: "This does not create a knowledge gap. It sends the concern into the existing diagnostic process for further investigation.",
      sent: "Sent for diagnostic review",
      oral_open: "Add oral evidence",
      oral_title: "Oral evidence",
      oral_desc: "Record what the student actually said (teacher observation) or a transcript. No audio recording, no speech-to-text, no pronunciation or fluency scoring.",
      oral_badge: "ORAL",
      oral_source: "Source",
      oral_teacher_obs: "Teacher observation",
      oral_transcript: "Transcript",
      oral_subject: "Subject",
      oral_text: "Original spoken production (unchanged)",
      oral_text_ph: "Type exactly what the student said …",
      oral_context: "Task/context (optional)",
      oral_submit: "Save oral evidence",
      oral_saved: "Oral evidence saved — the original is unchanged",
    },
    common: {
      loading: "Loading…",
      error: "Something went wrong. Please try again.",
    },
  },
};

// Principle chain steps (bilingual). Interface labels only — not curriculum content.
export const PRINCIPLE_STEPS = [
  { key: "evidence", sv: "Bevis", en: "Evidence", dsv: "All aktivitet börjar som bevis — betyg, prov, övningar, självrapporter.", den: "Everything begins as evidence — grades, tests, exercises, self-reports." },
  { key: "knowledge_state", sv: "Kunskapsläge", en: "Knowledge state", dsv: "Vad eleven faktiskt kan just nu, bortom betyget.", den: "What the student actually knows right now, beyond the grade." },
  { key: "gap", sv: "Lucka", en: "Gap", dsv: "Skillnaden mellan nuvarande läge och läroplanens mål.", den: "The distance between the current state and curriculum targets." },
  { key: "root_cause", sv: "Grundorsak", en: "Root cause", dsv: "Varför luckan finns — inte bara att den finns.", den: "Why the gap exists — not merely that it exists." },
  { key: "prerequisite", sv: "Förkunskap", en: "Prerequisite", dsv: "Vilka tidigare kunskaper som måste vara på plats först.", den: "Which earlier knowledge must be in place first." },
  { key: "intervention", sv: "Insats", en: "Intervention", dsv: "Rätt pedagogisk strategi — förklaring, exempel, analogi, övning.", den: "The right pedagogical strategy — explanation, example, analogy, practice." },
  { key: "practice", sv: "Övning", en: "Practice", dsv: "Trygg övning där misstag är värdefulla bevis.", den: "Safe practice where mistakes are valuable evidence." },
  { key: "reassessment", sv: "Omprövning", en: "Reassessment", dsv: "Ny bevisinsamling för att se om luckan stängts.", den: "Fresh evidence to see whether the gap has closed." },
  { key: "mastery", sv: "Bemästrande", en: "Mastery", dsv: "Bevisbaserat — upprepning, kontext, självständighet, kvarhållning.", den: "Evidence-based — repetition, context, independence, retention." },
  { key: "progression", sv: "Progression", en: "Progression", dsv: "Vidare mot högre nivåer — eleven definieras aldrig av ett F.", den: "Onward to higher levels — a student is never defined by an F." },
];
